import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/Button'
import type { ApprovalStatus } from './useAgencyStatus'

interface AgencyPendingScreenProps {
  approvalStatus: ApprovalStatus
  isLoading?: boolean
}

function PendingState() {
  return (
    <div className="flex flex-col items-center gap-5 text-center max-w-sm mx-auto py-12">
      {/* Animated clock / pulse icon */}
      <div
        className="flex h-[72px] w-[72px] items-center justify-center rounded-lg bg-warning/10 text-warning motion-safe:animate-pulse"
        aria-hidden
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="34" height="34">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      </div>

      <div>
        <Text variant="headline-sm" as="h1" className="mb-2">
          Your account is under review.
        </Text>
        <Text variant="body-md" color="muted" className="leading-relaxed">
          Our team is verifying your agency registration. You'll receive an email when your account
          is approved.
        </Text>
      </div>

      <div
        data-testid="agency-pending-what-to-expect"
        className="w-full rounded-[10px] border border-warning-muted bg-warning-subtle px-4 py-3 text-left"
      >
        <Text variant="label-sm" color="warning" className="mb-1">
          What happens next
        </Text>
        <Text variant="body-sm" color="muted">
          Once approved, you'll be able to create and manage client portfolios. Declined accounts
          can reapply after 30 days.
        </Text>
      </div>

      <Text variant="body-sm" color="subtle">
        This page refreshes automatically every 30 seconds.
      </Text>

      <a
        href="mailto:support@entite.io"
        data-testid="agency-pending-contact-btn"
        className="inline-flex items-center gap-1.5 text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded text-sm"
      >
        Contact support
      </a>
    </div>
  )
}

function DeclinedState() {
  return (
    <div className="flex flex-col items-center gap-5 text-center max-w-sm mx-auto py-12">
      <div
        className="flex h-[72px] w-[72px] items-center justify-center rounded-lg bg-danger/10 text-danger"
        aria-hidden
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="34" height="34">
          <circle cx="12" cy="12" r="9" />
          <path d="M15 9l-6 6M9 9l6 6" />
        </svg>
      </div>

      <div>
        <Text variant="headline-sm" as="h1" className="mb-2">
          Your agency application was declined.
        </Text>
        <Text variant="body-md" color="muted" className="leading-relaxed">
          We were unable to approve your agency registration at this time. Please check your email
          for details.
        </Text>
      </div>

      <div
        data-testid="agency-declined-detail"
        className="w-full rounded-[10px] border border-danger-muted bg-danger-subtle px-4 py-3 text-left"
      >
        <Text variant="label-sm" color="danger" className="mb-1">
          What you can do
        </Text>
        <Text variant="body-sm" color="muted">
          Contact our support team for more information, or reapply after 30 days if you believe
          this was an error.
        </Text>
      </div>

      <div className="flex gap-3 w-full">
        <Button
          data-testid="agency-declined-contact-btn"
          variant="outline"
          className="flex-1"
          onClick={() => { window.location.href = 'mailto:support@entite.io' }}
        >
          Contact support
        </Button>
      </div>
    </div>
  )
}

/**
 * Displayed to agency users whose registration is pending platform-admin approval,
 * or who have been declined. Polls /approvalStatus every 30s.
 */
export function AgencyPendingScreen({ approvalStatus, isLoading }: AgencyPendingScreenProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="agency-status-loading">
        <div
          role="status"
          aria-label="Loading approval status"
          className="h-8 w-8 motion-safe:animate-spin rounded-full border-4 border-primary border-t-transparent"
        />
      </div>
    )
  }

  if (approvalStatus === 'declined') return <DeclinedState />
  return <PendingState />
}
